import { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import Skeleton from '@mui/material/Skeleton'
import Typography from '@mui/material/Typography'
import CloseIcon from '@mui/icons-material/Close'
import CheckIcon from '@mui/icons-material/Check'

import { ConfirmationModalShell } from '@/shared/components/confirmation/ConfirmationModalShell'
import { NotificationRichTextEditor } from '@/shared/components/richtext'
import { richTextPlainLength } from '@/shared/components/richtext/richTextHelpers'
import { useToast } from '@/shared/toast/ToastProvider'

import { messageFromUnknownApiError } from '../lib/mapApiUnknownError'

import {
  useReminderPreview,
  useSendReminder,
  type ReminderChannel,
} from '../api/instrument-reminder'

const MESSAGE_MAX = 240
const CHANNELS: { id: ReminderChannel; label: string }[] = [
  { id: 'sms', label: 'SMS' },
  { id: 'email', label: 'Email' },
  { id: 'push', label: 'Push Notification' },
]

interface Props {
  open: boolean
  instrumentId: string
  onClose: () => void
}

function defaultHtml(message: string): string {
  return `<p>${message}</p>`
}

export function SendSurveyReminderDialog({ open, instrumentId, onClose }: Readonly<Props>) {
  const { showToast } = useToast()
  const preview = useReminderPreview(instrumentId, open)
  const send = useSendReminder(instrumentId)
  const [channels, setChannels] = useState<ReminderChannel[]>([])
  const [message, setMessage] = useState('')

  useEffect(() => {
    if (!open || !preview.data) return
    setChannels(preview.data.previousChannels)
    setMessage(defaultHtml(preview.data.defaultMessage))
  }, [open, preview.data])

  const plainLength = richTextPlainLength(message)
  const audienceEmpty = preview.data?.reminderAudience === 0
  const sendDisabled =
    preview.isLoading ||
    preview.isError ||
    audienceEmpty ||
    channels.length === 0 ||
    plainLength < 1 ||
    plainLength > MESSAGE_MAX ||
    send.isPending

  const toggle = (id: ReminderChannel) => {
    setChannels((current) =>
      current.includes(id) ? current.filter((channel) => channel !== id) : [...current, id],
    )
  }

  const onSend = () => {
    send.mutate(
      { channels, message },
      {
        onSuccess: () => {
          showToast('Reminder queued', 'success')
          onClose()
        },
        onError: (err) => showToast(messageFromUnknownApiError(err, 'Reminder could not be sent.'), 'error'),
      },
    )
  }

  return (
    <ConfirmationModalShell
      open={open}
      onClose={send.isPending ? undefined : onClose}
      dialogTestId="survey-reminder-dialog"
      ariaLabelledBy="survey-reminder-title"
      paperSx={{ width: 640, maxWidth: 'calc(100% - 32px)', borderRadius: '16px' }}
    >
      <Box sx={{ px: 3, pt: 3, pb: 2, borderBottom: '1px solid #e0e0e0', display: 'flex', gap: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography id="survey-reminder-title" sx={{ fontSize: '22px', fontWeight: 600, lineHeight: '33px', color: '#030213' }}>
            Send Survey Reminder
          </Typography>
          <Typography sx={{ fontSize: '13px', lineHeight: '19.5px', color: '#717182', mt: '4px' }}>
            Only eligible responders who have not completed this survey will receive this reminder.
          </Typography>
        </Box>
        <IconButton aria-label="Close reminder dialog" onClick={onClose} disabled={send.isPending} sx={{ width: 32, height: 32, border: '1px solid #e0e0e0', borderRadius: '10px' }}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      <Box sx={{ px: 3, pt: 2, pb: 1 }}>
        {preview.isLoading ? <ReminderPreviewSkeleton /> : null}
        {preview.isError ? (
          <Typography color="error" sx={{ fontSize: '13px', mb: 2 }}>
            Could not load the reminder audience.
          </Typography>
        ) : null}
        {preview.data ? <ReminderAudienceBlock preview={preview.data} /> : null}

        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#030213', mb: '16px' }}>
          Select Distribution Channels
        </Typography>
        <Box sx={{ display: 'flex', gap: '13px', mb: 2 }}>
          {CHANNELS.map((channel) => {
            const selected = channels.includes(channel.id)
            return (
              <Box
                key={channel.id}
                component="button"
                type="button"
                aria-pressed={selected}
                data-testid={`survey-reminder-channel-${channel.id}`}
                onClick={() => toggle(channel.id)}
                sx={{
                  flex: 1,
                  height: 45,
                  borderRadius: '14px',
                  border: '1px solid',
                  borderColor: selected ? '#008cd0' : '#e0e0e0',
                  bgcolor: selected ? '#edf1f8' : '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  px: '15px',
                  cursor: 'pointer',
                  font: 'inherit',
                }}
              >
                <Box
                  aria-hidden
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '4px',
                    border: '1px solid #008cd0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: selected ? '#008cd0' : '#fff',
                  }}
                >
                  {selected ? <CheckIcon sx={{ fontSize: 12, color: '#fff' }} /> : null}
                </Box>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#030213' }}>{channel.label}</Typography>
              </Box>
            )
          })}
        </Box>
        {preview.data?.previousChannelsLabel ? (
          <Typography data-testid="survey-reminder-previous-channels" sx={{ fontSize: '13px', color: '#717182', mb: 2 }}>
            {preview.data.previousChannelsLabel}
          </Typography>
        ) : null}

        <NotificationRichTextEditor
          value={message}
          onChange={setMessage}
          label="Reminder message"
          maxLength={MESSAGE_MAX}
          testIdPrefix="survey-reminder-message"
          helperText="This message will be sent through the selected channels"
          disabled={send.isPending || audienceEmpty}
        />
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', px: 3, py: '22px' }}>
        <Button variant="outlined" onClick={onClose} disabled={send.isPending} data-testid="survey-reminder-cancel" sx={{ height: 40, borderRadius: '12px', textTransform: 'none', borderColor: '#e5e7eb', color: '#6b6b6b' }}>
          Cancel
        </Button>
        <Button variant="contained" onClick={onSend} disabled={sendDisabled} data-testid="survey-reminder-send" sx={{ height: 40, minWidth: 141, borderRadius: '12px', textTransform: 'none', bgcolor: '#008cd0', '&:hover': { bgcolor: '#007ab8' } }}>
          {send.isPending ? <CircularProgress size={20} color="inherit" /> : 'Send Reminder'}
        </Button>
      </Box>
    </ConfirmationModalShell>
  )
}

function ReminderAudienceBlock({
  preview,
}: Readonly<{
  preview: {
    originalAudience: number
    completed: number
    reminderAudience: number
    blockedMessage: string | null
  }
}>) {
  if (preview.blockedMessage) {
    return (
      <Typography data-testid="survey-reminder-empty" sx={{ fontSize: '13px', color: '#030213', mb: 2 }}>
        {preview.blockedMessage}
      </Typography>
    )
  }
  return (
    <Box data-testid="survey-reminder-audience" sx={{ mb: 2 }}>
      {/* Business logic addition — not in Figma */}
      <Typography sx={{ fontSize: '13px', color: '#030213' }}>Original audience: {preview.originalAudience.toLocaleString()}</Typography>
      <Typography sx={{ fontSize: '13px', color: '#030213' }}>Completed: {preview.completed.toLocaleString()}</Typography>
      <Typography sx={{ fontSize: '13px', color: '#030213' }}>Reminder audience: {preview.reminderAudience.toLocaleString()}</Typography>
    </Box>
  )
}

function ReminderPreviewSkeleton() {
  return (
    <Box aria-busy="true" aria-label="Loading reminder audience" data-testid="survey-reminder-skeleton" sx={{ mb: 2 }}>
      <Skeleton variant="rounded" height={16} width="60%" sx={{ mb: 1 }} />
      <Skeleton variant="rounded" height={16} width="40%" />
    </Box>
  )
}
